import type { JSX } from "react";

type ICON_PROPS = { className?: string; size?: number };

const ICON_CLASS = "text-accent-green flex-shrink-0";
    
/** Public Domain mark icon (circle with "PD") – matches Creative Commons Public Domain mark. */
export function PublicDomainIcon({ className, size = 24 }: ICON_PROPS): JSX.Element {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width={size}
            height={size}
            className={className}
            aria-hidden
        >
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <text
                x="12"
                y="15"
                textAnchor="middle"
                fill="currentColor"
                fontSize="10"
                fontWeight="bold"
                fontFamily="system-ui, sans-serif"
            >
                PD
            </text>
        </svg>
    );
}

/** CC (Creative Commons) – official CC mark. */
function CcIcon({ className, size = 24 }: ICON_PROPS): JSX.Element {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="5.5 -3.5 64 64"
            width={size}
            height={size}
            className={className}
            aria-hidden
        >
            <circle fill="none" cx="37.785" cy="28.501" r="28.836" />
            <path
                fill="currentColor"
                d="M37.441-3.5c8.951,0,16.572,3.125,22.857,9.372c3.008,3.009,5.295,6.448,6.857,10.314c1.561,3.867,2.344,7.971,2.344,12.314c0,4.381-0.773,8.486-2.314,12.313c-1.543,3.828-3.82,7.21-6.828,10.143c-3.123,3.085-6.666,5.448-10.629,7.086c-3.961,1.638-8.057,2.457-12.285,2.457s-8.276-0.808-12.143-2.429c-3.866-1.618-7.333-3.961-10.4-7.027c-3.067-3.066-5.4-6.524-7-10.372S5.5,32.767,5.5,28.5c0-4.229,0.809-8.295,2.428-12.2c1.619-3.905,3.972-7.4,7.057-10.486C21.08-0.394,28.565-3.5,37.441-3.5z M37.557,2.272c-7.314,0-13.467,2.553-18.458,7.657c-2.515,2.553-4.448,5.419-5.8,8.6c-1.354,3.181-2.029,6.505-2.029,9.972c0,3.429,0.675,6.734,2.029,9.913c1.353,3.183,3.285,6.021,5.8,8.516c2.514,2.496,5.351,4.399,8.515,5.715c3.161,1.314,6.476,1.971,9.943,1.971c3.428,0,6.75-0.665,9.973-1.999c3.219-1.335,6.121-3.257,8.713-5.771c4.99-4.876,7.484-10.99,7.484-18.344c0-3.543-0.648-6.895-1.943-10.057c-1.293-3.162-3.18-5.98-5.654-8.458C50.984,4.844,44.795,2.272,37.557,2.272z M37.156,23.187l-4.287,2.229c-0.458-0.951-1.019-1.619-1.685-2c-0.667-0.38-1.286-0.571-1.858-0.571c-2.856,0-4.286,1.885-4.286,5.657c0,1.714,0.362,3.084,1.085,4.113c0.724,1.029,1.791,1.544,3.201,1.544c1.867,0,3.181-0.915,3.944-2.743l3.942,2c-0.838,1.563-2,2.791-3.486,3.686c-1.484,0.896-3.123,1.343-4.914,1.343c-2.857,0-5.163-0.875-6.915-2.629c-1.752-1.752-2.628-4.19-2.628-7.313c0-3.048,0.886-5.466,2.657-7.257c1.771-1.79,4.009-2.686,6.715-2.686C32.604,18.558,35.441,20.101,37.156,23.187z M55.613,23.187l-4.229,2.229c-0.457-0.951-1.02-1.619-1.686-2c-0.668-0.38-1.307-0.571-1.914-0.571c-2.857,0-4.287,1.885-4.287,5.657c0,1.714,0.363,3.084,1.086,4.113c0.723,1.029,1.789,1.544,3.201,1.544c1.865,0,3.18-0.915,3.941-2.743l4,2c-0.875,1.563-2.057,2.791-3.541,3.686c-1.486,0.896-3.105,1.343-4.857,1.343c-2.896,0-5.209-0.875-6.941-2.629c-1.736-1.752-2.602-4.19-2.602-7.313c0-3.048,0.885-5.466,2.658-7.257c1.77-1.79,4.008-2.686,6.713-2.686C51.117,18.558,53.938,20.101,55.613,23.187z"
            />
        </svg>
    );
}

/** BY (Attribution) – official CC BY person mark. */
function ByIcon({ className, size = 24 }: ICON_PROPS): JSX.Element {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="5.5 -3.5 64 64"
            width={size}
            height={size}
            className={className}
            aria-hidden
        >
            <circle fill="none" cx="37.637" cy="28.806" r="28.276" />
            <path
                fill="currentColor"
                d="M37.443-3.5c8.988,0,16.57,3.085,22.742,9.257C66.393,11.967,69.5,19.548,69.5,28.5c0,8.991-3.049,16.476-9.145,22.456C53.879,57.319,46.242,60.5,37.443,60.5c-8.649,0-16.153-3.144-22.514-9.43C8.644,44.784,5.5,37.262,5.5,28.5c0-8.761,3.144-16.342,9.429-22.742C21.101-0.415,28.604-3.5,37.443-3.5z M37.557,2.272c-7.276,0-13.428,2.553-18.457,7.657c-5.22,5.334-7.829,11.525-7.829,18.572c0,7.086,2.59,13.22,7.77,18.398c5.181,5.182,11.352,7.771,18.514,7.771c7.123,0,13.334-2.607,18.629-7.828c5.029-4.838,7.543-10.952,7.543-18.343c0-7.276-2.553-13.465-7.656-18.571C50.967,4.824,44.795,2.272,37.557,2.272z M46.129,20.557v13.085h-3.656v15.542h-9.944V33.643h-3.656V20.557c0-0.572,0.2-1.057,0.599-1.457c0.401-0.399,0.887-0.6,1.457-0.6h13.144c0.533,0,1.01,0.2,1.428,0.6C45.918,19.5,46.129,19.986,46.129,20.557z M33.042,12.329c0-3.008,1.485-4.514,4.458-4.514s4.457,1.504,4.457,4.514c0,2.971-1.486,4.457-4.457,4.457S33.042,15.3,33.042,12.329z"
            />
        </svg>
    );
}

/** SA (ShareAlike) – official CC ShareAlike mark. */
function SaIcon({ className, size = 24 }: ICON_PROPS): JSX.Element {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="5.5 -3.5 64 64"
            width={size}
            height={size}
            className={className}
            aria-hidden
        >
            <circle fill="none" cx="36.944" cy="28.631" r="29.105" />
            <path
                fill="currentColor"
                d="M37.443-3.5c8.951,0,16.531,3.105,22.742,9.315C66.393,11.987,69.5,19.548,69.5,28.5c0,8.954-3.049,16.457-9.145,22.514C53.918,57.338,46.279,60.5,37.443,60.5c-8.649,0-16.153-3.143-22.514-9.429C8.644,44.786,5.5,37.264,5.5,28.501c0-8.723,3.144-16.285,9.429-22.685C21.138-0.395,28.643-3.5,37.443-3.5z M37.557,2.272c-7.276,0-13.428,2.572-18.457,7.715c-5.22,5.296-7.829,11.467-7.829,18.513c0,7.125,2.59,13.257,7.77,18.4c5.181,5.182,11.352,7.771,18.514,7.771c7.123,0,13.334-2.609,18.629-7.828c5.029-4.876,7.543-10.99,7.543-18.343c0-7.313-2.553-13.485-7.656-18.513C51.004,4.842,44.832,2.272,37.557,2.272z M23.271,23.985c0.609-3.924,2.189-6.962,4.742-9.114c2.552-2.152,5.656-3.228,9.314-3.228c5.027,0,9.029,1.62,12,4.856c2.971,3.238,4.457,7.391,4.457,12.457c0,4.915-1.543,9-4.627,12.256c-3.088,3.256-7.086,4.886-12.002,4.886c-3.619,0-6.743-1.085-9.371-3.257c-2.629-2.172-4.209-5.257-4.743-9.257H31.1c0.19,3.886,2.533,5.829,7.029,5.829c2.246,0,4.057-0.972,5.428-2.914c1.373-1.942,2.059-4.534,2.059-7.771c0-3.391-0.629-5.971-1.885-7.743c-1.258-1.771-3.066-2.657-5.43-2.657c-4.268,0-6.667,1.885-7.2,5.656h2.343l-6.342,6.343l-6.343-6.343L23.271,23.985L23.271,23.985z"
            />
        </svg>
    );
}

const ICONS_WRAPPER_STYLE = { display: "inline-flex" as const, alignItems: "center" as const, gap: 2 };

/** CC BY (Creative Commons Attribution) – CC and BY as separate icons. */
export function CcByIcon({ className, size = 24 }: ICON_PROPS): JSX.Element {
    const iconSize = size / 2;
    return (
        <span className={className} style={ICONS_WRAPPER_STYLE}>
            <CcIcon size={iconSize} />
            <ByIcon size={iconSize} />
        </span>
    );
}

/** CC BY-SA (Creative Commons Attribution-ShareAlike) – CC, BY and SA as separate icons. */
export function CcBySaIcon({ className, size = 24 }: ICON_PROPS): JSX.Element {
    const iconSize = size / 3;
    return (
        <span className={className} style={ICONS_WRAPPER_STYLE}>
            <CcIcon size={iconSize} />
            <ByIcon size={iconSize} />
            <SaIcon size={iconSize} />
        </span>
    );
}

/** Redirect / external link icon – opens in new tab. */
export function RedirectIcon({ className, size = 18 }: ICON_PROPS): JSX.Element {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width={size}
            height={size}
            className={className}
            aria-hidden
        >
            <path
                fill="currentColor"
                d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7zm-1 4V5H5v14h14v-8h-2v6H7V7h6z"
            />
        </svg>
    );
}

export const PUBLIC_DOMAIN_URL = "http://creativecommons.org/publicdomain/mark/1.0/";

/** Creative Commons Attribution – common license URLs (with/without trailing slash, http/https). */
export const CC_BY_URLS = [
    "https://creativecommons.org/licenses/by/4.0/",
    "https://creativecommons.org/licenses/by/4.0",
    "http://creativecommons.org/licenses/by/4.0/",
    "http://creativecommons.org/licenses/by/4.0",
    "https://creativecommons.org/licenses/by/3.0/",
    "https://creativecommons.org/licenses/by/3.0",
    "http://creativecommons.org/licenses/by/3.0/",
    "http://creativecommons.org/licenses/by/3.0",
] as const;

/** Creative Commons Attribution-ShareAlike – common license URLs. */
export const CC_BY_SA_URLS = [
    "https://creativecommons.org/licenses/by-sa/4.0/",
    "https://creativecommons.org/licenses/by-sa/4.0",
    "http://creativecommons.org/licenses/by-sa/4.0/",
    "http://creativecommons.org/licenses/by-sa/4.0",
    "https://creativecommons.org/licenses/by-sa/3.0/",
    "https://creativecommons.org/licenses/by-sa/3.0",
    "http://creativecommons.org/licenses/by-sa/3.0/",
    "http://creativecommons.org/licenses/by-sa/3.0",
] as const;

export type RightsStatementInfo = {
    name: string;
    Icon: (props: ICON_PROPS) => JSX.Element;
};

const CC_BY_INFO: RightsStatementInfo = {
    name: "CC BY",
    Icon: CcByIcon,
};

const CC_BY_SA_INFO: RightsStatementInfo = {
    name: "CC BY-SA",
    Icon: CcBySaIcon,
};

export const RIGHTS_STATEMENTS: Record<string, RightsStatementInfo> = {
    [PUBLIC_DOMAIN_URL]: {
        name: "Public Domain",
        Icon: PublicDomainIcon,
    },
    ...Object.fromEntries(CC_BY_URLS.map((url) => [url, CC_BY_INFO])),
    ...Object.fromEntries(CC_BY_SA_URLS.map((url) => [url, CC_BY_SA_INFO])),
};

export function getRightsStatementInfo(url: string): RightsStatementInfo | null {
    return RIGHTS_STATEMENTS[url] ?? null;
}

/** Returns true if the string looks like a URL (starts with http). */
export function isRightsUrl(value: string): boolean {
    return typeof value === "string" && (value.startsWith("http://") || value.startsWith("https://"));
}

export type RightsStatementLinkProps = {
    rightsUrl: string;
    className?: string;
};

/** Rights statement: shows statement icon + name; only the redirect icon opens the link in a new tab. */
export function RightsStatementLink({ rightsUrl, className }: RightsStatementLinkProps): JSX.Element {
    const info = getRightsStatementInfo(rightsUrl);
    const wrapperClassName = [
        "d-flex align-items-center gap-2 text-white-50 text-break",
        className ?? "",
    ].filter(Boolean).join(" ");

    return (
        <span className={wrapperClassName}>
            {info ? (
                <>
                    <info.Icon className={ICON_CLASS} size={18} />
                    <span>{info.name}</span>
                    <a
                        href={rightsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${ICON_CLASS} ms-1 text-decoration-none`}
                        aria-label="Open rights statement in new tab"
                    >
                        <RedirectIcon size={16} />
                    </a>
                </>
            ) : (
                <>
                    <span>{rightsUrl}</span>
                    {isRightsUrl(rightsUrl) && (
                        <a
                            href={rightsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${ICON_CLASS} ms-1 text-decoration-none`}
                            aria-label="Open rights statement in new tab"
                        >
                            <RedirectIcon size={16} />
                        </a>
                    )}
                </>
            )}
        </span>
    );
}
